import { cva } from 'class-variance-authority';

// Select types
export const SELECT_STATE = {
  DEFAULT: 'default',
  FILLED: 'filled',
  HOVER: 'hover',
  DISABLED: 'disabled',
  FILLED_HOVER: 'filled-hover'
} as const;

export const SELECT_OPTION_ARRANGEMENT = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical'
} as const;

export const SELECT_OPTION_TYPE = {
  DEFAULT: 'default',
  LEADING_ICON: 'leading-icon',
  DIVIDER: 'divider',
  SECTION_TITLE: 'section-title',
  BUTTON: 'button'
} as const;

export const SELECT_OPTION_STATE = {
  DEFAULT: 'default',
  HOVER: 'hover',
  DISABLED: 'disabled'
} as const;

export type SelectState = (typeof SELECT_STATE)[keyof typeof SELECT_STATE];
export type SelectOptionArrangement =
  (typeof SELECT_OPTION_ARRANGEMENT)[keyof typeof SELECT_OPTION_ARRANGEMENT];
export type SelectOptionType = (typeof SELECT_OPTION_TYPE)[keyof typeof SELECT_OPTION_TYPE];
export type SelectOptionState = (typeof SELECT_OPTION_STATE)[keyof typeof SELECT_OPTION_STATE];

// Select trigger variants
export const selectLabelVariants = cva('flex items-center gap-3xs h-xl', {
  variants: {},
  defaultVariants: {}
});

export const selectLabelTextVariants = cva('text-sm leading-4xs font-medium text-primary');

export const selectTriggerVariants = cva(
  'flex justify-between items-center gap-2xs h-6xl py-3xs pr-xs pl-s rounded-sm border transition-colors cursor-pointer',
  {
    variants: {
      state: {
        default: 'bg-primary border-primary text-primary hover:border-hover',
        filled: 'bg-primary border-primary text-primary hover:border-hover',
        hover: 'bg-primary border-hover text-primary',
        disabled: 'bg-gray-75 border-disabled text-tertiary cursor-not-allowed pointer-events-none',
        'filled-hover': 'bg-primary border-hover text-primary'
      },
      open: {
        true: '!border-focus',
        false: ''
      }
    },
    defaultVariants: {
      state: 'default',
      open: false
    }
  }
);

export const selectContentVariants = cva('flex items-center gap-2xs flex-1 min-w-0 overflow-hidden');

export const selectTextVariants = cva('text-sm leading-md font-regular truncate', {
  variants: {
    filled: {
      true: 'text-primary',
      false: 'text-secondary'
    }
  },
  defaultVariants: {
    filled: false
  }
});

export const selectIconsWrapperVariants = cva('flex items-center gap-2xs');

export const selectIconVariants = cva('flex items-center justify-center w-l h-l flex-shrink-0 transition-transform duration-200', {
  variants: {
    open: {
      true: 'rotate-180',
      false: ''
    }
  },
  defaultVariants: {
    open: false
  }
});

// Select menu (panel) variants
export const selectMenuVariants = cva(
  'bg-primary border border-tertiary rounded-sm shadow-lg py-5xs',
  {
    variants: {},
    defaultVariants: {}
  }
);

// Select option (item) wrapper - 2-layer structure
export const selectOptionWrapperVariants = cva('px-4xs py-5xs', {
  variants: {
    type: {
      default: '',
      'leading-icon': '',
      divider: 'px-4xs py-0',
      'section-title': '',
      button: ''
    }
  },
  defaultVariants: {
    type: 'default'
  }
});

// Select option content
export const selectOptionContentVariants = cva(
  'flex items-center gap-2xs px-s py-3xs text-sm leading-md font-regular cursor-pointer transition-colors',
  {
    variants: {
      arrangement: {
        horizontal: 'flex-row justify-between',
        vertical: 'flex-row items-center justify-between'
      },
      state: {
        default: 'text-primary bg-primary hover:bg-hover hover:rounded-xs rounded-sm',
        hover: 'text-primary bg-hover rounded-xs',
        disabled: 'text-tertiary bg-primary cursor-not-allowed pointer-events-none rounded-sm'
      }
    },
    defaultVariants: {
      arrangement: 'horizontal',
      state: 'default'
    }
  }
);

// Section title variant
export const selectSectionTitleVariants = cva(
  'flex items-center gap-2xs px-s py-3xs rounded-sm text-xs leading-xs font-semibold text-secondary'
);

// Divider
export const selectDividerVariants = cva('h-0 border-t border-tertiary py-5xs gap-2xs');

// Supporting text
export const selectSupportingTextVariants = cva(
  'text-xs leading-xs font-regular text-tertiary w-full'
);

// Icon wrapper for options
export const selectOptionIconVariants = cva('flex items-center justify-center w-l h-l flex-shrink-0');

// Vertical content wrapper
export const selectVerticalContentVariants = cva('flex flex-col justify-center flex-1');

// Check icon for selected options
export const selectCheckIconVariants = cva('inline-flex items-center justify-center w-l h-l flex-shrink-0 ml-2xs');

// Button type option variants
export const selectOptionButtonVariants = cva(
  'flex items-center gap-3xs px-s py-3xs text-sm leading-md font-semibold cursor-pointer transition-colors rounded-xs',
  {
    variants: {
      state: {
        default: 'text-primary bg-primary hover:bg-hover',
        hover: 'text-primary bg-hover'
      }
    },
    defaultVariants: {
      state: 'default'
    }
  }
);

export const selectOptionButtonIconVariants = cva('inline-flex items-center justify-center w-l h-l flex-shrink-0');
